import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  TextField,
  Collapse,
  Tooltip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { AgentDraft, AgentDraftType } from '~/types/coordinate';

const DRAFT_LABELS: Record<AgentDraftType, string> = {
  provider_outreach: 'Provider Outreach',
  patient_explanation: 'Patient Explanation Letter',
  family_update: 'Family Update',
  agency_narrative: 'Agency Referral Narrative',
  start_of_care_followup: 'Start of Care Follow-Up',
};

const DRAFT_DESCRIPTIONS: Record<AgentDraftType, string> = {
  provider_outreach: 'Ready to the matched provider to confirm capacity and initiate referral.',
  patient_explanation: 'Ready to the patient explaining the home care discharge plan.',
  family_update: 'Ready to the primary family contact with a referral status update.',
  agency_narrative: 'Clinical narrative cover letter to accompany the referral packet — summarizes the patient story, skilled needs, and what success looks like for the agency.',
  start_of_care_followup: 'Follow-up message to the agency requesting confirmation of the first home visit date and assigned clinician.',
};

const CHANNEL_LABELS: Record<'portal' | 'email', string> = {
  portal: 'Via EHR Patient Portal',
  email: 'Via Email',
};

interface Props {
  draft: AgentDraft;
  onUse: () => void;
  onEdit: (content: string) => void;
  onDismiss: () => void;
}

const AgentDraftCard: React.FC<Props> = ({ draft, onUse, onEdit, onDismiss }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(draft.content);

  const handleSaveEdit = () => {
    onEdit(editContent);
    setEditing(false);
  };

  const handleCancelEdit = () => {
    setEditContent(draft.content);
    setEditing(false);
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'rgba(99, 95, 153, 0.2)',
        borderRadius: 1.5,
        bgcolor: '#f0eef8',
        p: 2,
        mb: 1.5,
      }}
    >
      {/* Header row — always visible, click to expand/collapse */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => !editing && setExpanded(v => !v)}
        sx={{ cursor: editing ? 'default' : 'pointer', mb: expanded || editing ? 1.5 : 0 }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 16, color: '#635f99', flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography variant="caption" fontWeight={700} color="#635f99">
              Coordinate AI
            </Typography>
            <Chip
              label={DRAFT_LABELS[draft.type]}
              size="small"
              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#f0eef8', color: '#4a4776' }}
            />
            {draft.channel && (
              <Chip
                label={CHANNEL_LABELS[draft.channel]}
                size="small"
                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, bgcolor: '#e0f7f4', color: '#006e60' }}
              />
            )}
            <Tooltip title={DRAFT_DESCRIPTIONS[draft.type]} placement="top" arrow>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: '#635f99', cursor: 'default' }} onClick={e => e.stopPropagation()} />
            </Tooltip>
          </Stack>
        </Box>
        {!editing && (
          <Typography variant="caption" sx={{ color: '#635f99', flexShrink: 0, fontSize: '0.7rem' }}>
            {expanded ? 'Collapse ▲' : 'View draft ▼'}
          </Typography>
        )}
      </Stack>

      <Collapse in={(expanded || editing) && !editing}>
        <Box
          sx={{
            bgcolor: '#fff',
            border: '1px solid',
            borderColor: 'rgba(99, 95, 153, 0.12)',
            borderRadius: 1,
            p: 1.5,
            mb: 1.5,
            maxHeight: 340,
            overflowY: 'auto',
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: '0.78rem', whiteSpace: 'pre-wrap', color: 'text.secondary', lineHeight: 1.6 }}
          >
            {draft.content}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            onClick={onUse}
            sx={{ fontSize: '0.75rem', bgcolor: '#635f99', '&:hover': { bgcolor: '#4a4776' } }}
          >
            {draft.channel === 'portal' ? 'Send via Portal' : draft.channel === 'email' ? 'Send via Email' : 'Use'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setEditing(true)}
            sx={{ fontSize: '0.75rem', color: '#635f99', borderColor: 'rgba(99, 95, 153, 0.35)' }}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={onDismiss}
            sx={{ fontSize: '0.75rem', color: 'text.disabled', ml: 'auto !important' }}
          >
            Dismiss
          </Button>
        </Stack>
      </Collapse>

      <Collapse in={editing}>
        <TextField
          fullWidth
          multiline
          rows={8}
          value={editContent}
          onChange={e => setEditContent(e.target.value)}
          size="small"
          sx={{ mb: 1.5, '& .MuiInputBase-input': { fontSize: '0.78rem', lineHeight: 1.6 } }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            onClick={handleSaveEdit}
            sx={{ fontSize: '0.75rem', bgcolor: '#635f99', '&:hover': { bgcolor: '#4a4776' } }}
          >
            Save
          </Button>
          <Button size="small" variant="outlined" onClick={handleCancelEdit} sx={{ fontSize: '0.75rem' }}>
            Cancel
          </Button>
        </Stack>
      </Collapse>
    </Box>
  );
};

export default AgentDraftCard;
